// https://vuejs.org/v2/guide/plugins.html

import _Vue from 'vue'
import Router from 'vue-router';
import * as utils from '../utils'
import router from '../router';      // not sure why this.$router doesn't work (I think because of the way main.ts uses this plugin)
import store from '../store';        // not sure why this.$store doesn't work (I think because of the way main.ts uses this plugin)
import { RawLocation } from 'vue-router';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { FBAuthResponse } from '../User'
import { StoryUser } from '../StoryUser'
import { globalUC } from '../UserCache';
import * as utils_auth from '../utils_auth';

// IMPORTANT: broken inside Android FB in-app browser
//  https://stackoverflow.com/questions/59135674/facebook-login-is-not-working-in-andriod-facebook-in-app-browser
//  https://stackoverflow.com/questions/58939502/facebook-javascript-sdk-fb-login-not-working-on-facebook-built-in-browser
//  https://stackoverflow.com/questions/37269888/facebook-login-is-not-working-in-app-browser-for-ios
//  Was able to fix it by using 1st link
/*
status specifies the login status of the person using the app. The status can be one of the following:
  connected - the person is logged into Facebook, and has logged into your app.
  not_authorized - the person is logged into Facebook, but has not logged into your app.
  unknown - the person is not logged into Facebook, so you don't know if they've logged into your app or FB.logout() was called before and therefore, it cannot connect to Facebook.
authResponse is included if the status is connected and is made up of the following:
  accessToken - contains an access token for the person using the app.
  expiresIn - indicates the UNIX time when the token expires and needs to be renewed.
  signedRequest - a signed parameter that contains information about the person using the app.
  userID - the ID of the person using the app.
*/

const APP_ID = process.env.VUE_APP_FB_APP_ID
const SCOPE = 'public_profile,email'

if (!APP_ID) {
  const e = 'VUE_APP_FB_APP_ID is null from environment (check you .env and .env.local)'
  console.error(e)
  throw(e)
}

@Component({
  name: 'FacebookPlugin',
})
export class FacebookPlugin extends Vue {
  // public member variables
  public uid: string = null                                         // not really used by client, but good to have I guess
  public accessToken: string = null                                 // not really used by client, but good to have I guess
  public isLoggedIntoFacebook: boolean = false                      // user's browser is logged into FB, but not into StoryCircle
  public isLoggedIntoStoryCircleViaFacebook: boolean = false        // user is logged into both FB and StoryCircle
  public useFbLoginRandomAB: boolean = Math.random() < 1.0          // in case app wants to A/B test this (update: 02/19/2020: even for lander, way better for home and blog but low samples). Update - changed to always true

  // public member functions
  // App.vue calls await this.$fb.init() in it's created() hook
  public async init() {
    // @ts-ignore:
    await Vue.loadScript(`https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v6.0&appId=${APP_ID}&autoLogAppEvents=1`);

    if (store.state.devmode) console.log(`FacebookPlugin.init() loaded FB SDK`)

    this.queryFB()
    this.subscribeToFBEvents()
  }

  // components call this (e.g from a button click) to show FB's popup login dialog
  public login(): void {

    // the reason that I call FB.getLoginStatus() before calling FB.login() is because of an Android in-app browser bug
    //  https://stackoverflow.com/questions/59135674/facebook-login-is-not-working-in-andriod-facebook-in-app-browser
    // still doesn't work super great because it requires user to click FB login twice
    (window as any).FB.getLoginStatus(async (response: FBAuthResponse) => {
      this.setLocalsFromFBAuthResponse(response, 'login() getLoginStatus()');
      if (response.status !== 'connected') {
        (window as any).FB.login(async (response: FBAuthResponse) => {
          this.setLocalsFromFBAuthResponse(response, 'login() login()')
          if (response.status === 'connected') {
            await this.sendAuthResponseToServer(response)
          } else {
            // TODO: tell user something went wrong?
          }
         },
         {
           scope: SCOPE,
           return_scopes: true
         }
         );
    
      } else {
        await this.sendAuthResponseToServer(response)
      }
     });


  }

  // private member functions

  // called from init()
  private queryFB(): void {
    (window as any).FB.getLoginStatus((response: FBAuthResponse) => {
      this.setLocalsFromFBAuthResponse(response, 'queryFB()')
     });
  }

  // called from init()
  private subscribeToFBEvents(): void {
    // This event is fired when your app first notices the user (in other words, gets a session when it didn't already have a valid one).
    (window as any).FB.Event.subscribe('auth.login', (response: FBAuthResponse) => { this.setLocalsFromFBAuthResponse(response, 'event auth.login') });

    // This event is fired when your app notices that there is no longer a valid user (in other words, it had a session but can no longer validate the current user).
    (window as any).FB.Event.subscribe('auth.logout', (response: FBAuthResponse) => { this.setLocalsFromFBAuthResponse(response, 'event auth.logout') });

    // This event is fired when your app notices that there is no longer a valid user (in other words, it had a session but can no longer validate the current user).
    (window as any).FB.Event.subscribe('auth.authResponseChange', (response: FBAuthResponse) => { this.setLocalsFromFBAuthResponse(response, 'event auth.authResponseChange') });

    // Typically you will want to use the auth.authResponseChange event. But in rare cases, you want to distinguish between these three states:
    // (window as any).FB.Event.subscribe('auth.statusChange', (response: FBAuthResponse) => { this.setLocalsFromFBAuthResponse(response, 'event auth.statusChange') });

  }

  // called to set our local public members
  private setLocalsFromFBAuthResponse(response: FBAuthResponse, caller: string) {
    if (store.state.devmode) console.log(`setLocalsFromFBAuthResponse() from ${caller}`, response)

    if (response.status === 'connected') {
      // The user is logged in and has authenticated your app, and response.authResponse supplies the user's ID, a valid access token, a signed request, and the time the access token and signed request each expire.
      this.uid = response.authResponse.userID;
      this.accessToken = response.authResponse.accessToken;
      this.isLoggedIntoFacebook = true
      this.isLoggedIntoStoryCircleViaFacebook = true
    } else if (response.status === 'not_authorized') {
      // The user hasn't authorized your application.  They must click the Login button, or you must call FB.login in response to a user gesture, to launch a login dialog.
      this.isLoggedIntoFacebook = true
      this.isLoggedIntoStoryCircleViaFacebook = false
    } else {
      // The user isn't logged in to Facebook. You can launch a login dialog with a user gesture, but the user may have to log in to Facebook before authorizing your application.
      this.isLoggedIntoFacebook = false
      this.isLoggedIntoStoryCircleViaFacebook = false
    }
  }

  // send login details to server
  private async sendAuthResponseToServer(fbAuthResponse: FBAuthResponse) {

    const utm_source: string = localStorage.getItem('utm_source')
    const utm_campaign: string = localStorage.getItem('utm_campaign')
    const utm_medium: string = localStorage.getItem('utm_medium')
    const utm_content: string = localStorage.getItem('utm_content')
    const utm_term: string = localStorage.getItem('utm_term')

    // make POST call to server
    try {
      store.commit('setLoading', { loading: true });
      const url: string = utils.getURLprefix() + '/api/auth/handleFbAuthResponse';
      const response: any = await utils.makePostCall(url, { fbAuthResponse, utm_source, utm_campaign, utm_medium, utm_content, utm_term } );
      if (!response || !response.data) throw(new Error('FacebookPlugin.sendAuthResponseToServer() got null response or null response.data'))
      if (response.data.error) {
        store.commit('setLoading', { loading: false });

        if (response.data.error.includes('email address required')) {
          (window as any).analytics.track('Email Missing')
        }

        utils.toast(this, 'Error', response.data.error)

        // This user either disallowed email or FB account doesn't have an email address, so let's disable FB login for them
        this.isLoggedIntoFacebook = false
        this.isLoggedIntoStoryCircleViaFacebook = false
      }
      if (response.data.user && response.data.sessionId) {
        // success
        store.commit('setLoading', { loading: false });

        const user: StoryUser = new StoryUser(response.data.user)
        utils_auth.saveAuthStuffToStoreAndLocalStorage(user, response.data.sessionId)
        globalUC.add(user);     // might as well give cache the very latest one

        // utils.toast(this, 'Success', `Login successful for ${user.username || user.email}`);

        (window as any).analytics.alias(user.getIdAsString());  // tie the previously anon user to this newly id'd user

        // only if user registered new, and not merely logged in
        if (new Date().getTime() - user.created.getTime() < 10 * 60 * 1000) {
          (window as any).analytics.track('User Registered')
        }

        // go to subsequent page
        try {
          const whereToGo: RawLocation = utils_auth.getLocationAfterLogin()
          if (whereToGo) {
            await router.push(whereToGo);
            utils_auth.setLocationAfterLogin(null)
          } else {
            await router.push({ name: 'home' })
          }
        } catch (Error) {
          console.log(Error)
        }

        // start tutorial in case it's appropriate
        store.commit('setInTutorial', { inTutorial: true });
        await router.go(0)

      }

    } catch (error) {
      utils.toast(this, 'System Error', 'Could not login')
      if (store.state.devmode) console.error(error);
    } finally {
      store.commit('setLoading', { loading: false });
    }

  }
}

export class PlugOptions {
  // add stuff
}

// https://vuejs.org/v2/guide/plugins.html#Writing-a-Plugin
export function facebookPluginInstallFn(Vue: typeof _Vue, options?: PlugOptions): void {
  // 4. add an instance method
  Vue.prototype.$fb = new FacebookPlugin();
}
